import { useState, useRef, useEffect, useMemo } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Undo2, Search, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { fetchVolumeData, fetchVolumeDates, fetchBrokerVolumeData } from '../../utils/volumeBlob';
import { fetchAccumulationDistributionData, fetchBrokerAccumulationDistributionData, type AccumulationDistributionType } from '../../utils/accumulationDistributionBlob';
import { fetchBrokerList } from '../../utils/brokerMetadataBlob';
import { evaluateFilterExpression, colorTokenCategory, parseNumericCondition, parseCrossColumnReference, evaluateCrossColumn, type FilterCell, type AtomEvaluator, type CrossRefCondition } from '../../utils/columnFilter';

// Broker colors same as BrokerSummaryPage (merah=foreign, ijo=government, putih=local)
const FOREIGN_BROKERS = ["AG","AH","AI","AK","BK","BQ","CG","CS","DP","DR","DU","FS","GW","HD","KK","KZ","LH","LG","LS","MS","RB","RX","TX","YP","YU","ZP"];
const GOVERNMENT_BROKERS = ['CC','NI','OD','DX'];
const getBrokerColorClass = (code: string): string => {
  if (GOVERNMENT_BROKERS.includes(code)) return 'text-green-500 font-semibold';
  if (FOREIGN_BROKERS.includes(code)) return 'text-red-400 font-semibold';
  return 'text-white font-semibold';
};

export interface AccumulationData {
  symbol: string;
  price: number;
  volume: number;
  value: number;
  percent1d: number;
  ma20: number;
  ma22: number;
  ma30: number;
  vol_ma22: number;
  vol_ma30: number;
  ma22_prev: number;
  ma30_prev: number;
  price_color: number;
  vol_color: number;
  trend: string;
  momentum: number;
  insider?: number;
  periods: Record<string, {
    val: number;
    vol: number;
    val_pct: number;
    vol_pct: number;
    comp: number;
    ma40: number;
    chg: number;
    xl: number;
    xc: number;
  }>;
}

// Convert backend JSON from CSV to frontend format
const convertBackendToFrontend = (backendData: any[]): AccumulationData[] => {
  const uniqueItems = new Map<string, any>();
  for (const item of backendData) {
    const symbol = item.Stock || item.Ticker || '';
    if (symbol && !uniqueItems.has(symbol)) {
      uniqueItems.set(symbol, item);
    }
  }

  return Array.from(uniqueItems.values()).map(item => {
    const symbol = item.Stock || item.Ticker || '';
    const price = Number(item.price) || 0;
    const volume = Number(item.volume) || 0;
    const value = Number(item.value) || 0;
    const percent1d = Number(item.price_chg) || 0;
    const ma20 = Number(item.ma20_above) || 0;
    const ma22 = Number(item.ma22) || 0;
    const ma30 = Number(item.ma30) || 0;
    const vol_ma22 = Number(item.vol_ma22) || 0;
    const vol_ma30 = Number(item.vol_ma30) || 0;
    const ma22_prev = Number(item.ma22_prev) || 0;
    const ma30_prev = Number(item.ma30_prev) || 0;
    const price_color = Number(item.price_color) || 0;
    const vol_color = Number(item.vol_color) || 0;
    const momentum = item.momentum !== undefined ? Number(item.momentum) : 1; // Default SW
    const insider = item.insider !== undefined ? Number(item.insider) : undefined;
    
    // Backend returns 0 (Down), 1 (Sideways), 2 (Up)
    let trend = 'SW';
    if (item.trend !== undefined) {
      const t = Number(item.trend);
      if (t === 2) trend = 'Up Trend';
      else if (t === 0) trend = 'Down Trend';
    }

    const periods: Record<string, { val: number; vol: number; val_pct: number; vol_pct: number; comp: number; ma40: number; chg: number; xl: number; xc: number; }> = {};
    
    // Extract all period prefixes (10 kolom hari d0..d9, tanpa weekly)
    const periodPrefixes = [
      'd0','d1','d2','d3','d4','d5','d6','d7','d8','d9',
      'fd0','fd1','fd2','fd3','fd4','fd5','fd6','fd7','fd8','fd9',
      'cd0','cd1','cd2','cd3','cd4','cd5','cd6','cd7','cd8','cd9',
      'cfd0','cfd1','cfd2','cfd3','cfd4','cfd5','cfd6','cfd7','cfd8','cfd9'
    ];

    for (const p of periodPrefixes) {
      periods[p] = {
        val: Number(item[`${p}_val`]) || 0,
        vol: Number(item[`${p}_vol`]) || 0,
        val_pct: Number(item[`${p}_val_pct`]) || 0,
        vol_pct: Number(item[`${p}_vol_pct`]) || 0,
        comp: Number(item[`${p}_compat`]) || 0,
        ma40: Number(item[`${p}_ma40`]) || 0,
        chg: Number(item[`${p}_chg`]) || 0,
        xl: item[`${p}_xl`] !== undefined ? Number(item[`${p}_xl`]) : -1,
        xc: item[`${p}_xc`] !== undefined ? Number(item[`${p}_xc`]) : -1,
      };
    }

    return {
      symbol,
      price,
      volume,
      value,
      percent1d,
      ma20,
      ma22,
      ma30,
      vol_ma22,
      vol_ma30,
      ma22_prev,
      ma30_prev,
      price_color,
      vol_color,
      trend,
      momentum,
      insider,
      periods
    };
  });
};

const formatNumberWithAbbreviation = (num: number | null): string => {
  if (num === null || num === 0) return '-';
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  if (absNum >= 1e9) {
    return sign + (absNum / 1e9).toFixed(1) + 'B';
  } else if (absNum >= 1e6) {
    return sign + (absNum / 1e6).toFixed(1) + 'M';
  } else if (absNum >= 1e3) {
    return sign + (absNum / 1e3).toFixed(1) + 'K';
  } else {
    return sign + absNum.toFixed(1); 
  }
};

// Warna sel periode: tier ratio (>5 / 2-5 / <2) × arah chg periode —
// dihitung frontend dari data langsung; kode compat backend tidak dipakai.
// chg >= 0: >5 hijau, 2-5 lime | chg < 0: >5 merah, 2-5 oren | <2 netral.
// Sel merah/oren diberi tanda minus pada nilai (arah turun); filter/sort tetap pakai angka asli.
const getPeriodRatioColor = (ratio: number, chg: number): string => {
  if (ratio > 5) return chg < 0 ? 'text-[#ff0000]' : 'text-[#00ff00]';
  if (ratio >= 2) return chg < 0 ? 'text-[#ffa500]' : 'text-[#b2ff00]';
  return 'text-gray-300';
};

// Warna % chg & Price: merah turun, hijau naik, netral datar (tanpa lime/oren).
const getPercentChgColor = (chg: number): string => {
  if (chg > 0) return 'text-[#00ff00]';
  if (chg < 0) return 'text-[#ff0000]';
  return 'text-gray-300';
};

// Nilai sesuai tampilan: sel merah/oren (arah turun) bernilai negatif utk sort & filter.
const signedByColor = (val: number, color: string): number =>
  val > 0 && (color === 'text-[#ff0000]' || color === 'text-[#ffa500]') ? -val : val;

// Kolom A/D = kolom D0 di menu Acc/Dist (sumber acc_dist.zip, bukan volume.zip).
type AccD0 = { val: number; vol_pct: number; comp: number; xl: number; xc: number };

const extractAccD0 = (rows: Record<string, unknown>[]): Record<string, AccD0> => {
  const map: Record<string, AccD0> = {};
  for (const item of rows) {
    const s = String(item['Ticker'] ?? item['Stock'] ?? '');
    if (!s) continue;
    map[s] = {
      val: Number(item['d0_val']) || 0,
      vol_pct: Number(item['d0_vol_pct']) || 0,
      comp: Number(item['d0_compat']) || 0,
      xl: item['d0_xl'] !== undefined ? Number(item['d0_xl']) : -1,
      xc: item['d0_xc'] !== undefined ? Number(item['d0_xc']) : -1,
    };
  }
  return map;
};

// Warna sel A/D — identik dgn getTextColor di StoryAccumulationDistribution (gate comp/xl/xc).
const getAccTextColor = (val: number, comp: number, xl: number, xc: number, pct: number): string => {
  if (val < 0) return (comp > 0 && xl === 0 && xc === 0 && pct < -4) ? 'text-[#ff0000]' : 'text-[#ffa500]';
  if (val > 0) return (comp < 0 && xl === 1 && xc === 1 && pct > 4) ? 'text-[#00ff00]' : 'text-[#b2ff00]';
  return 'text-muted-foreground';
};

const getAccColor = (d0: AccD0 | undefined, valueFilter: string): string => {
  if (!d0) return 'text-gray-600';
  const val = valueFilter === 'Volume' ? d0.val : d0.vol_pct;
  if (val === 0) return 'text-gray-600';
  return getAccTextColor(val, d0.comp, d0.xl, d0.xc, d0.vol_pct);
};

const getPercent1dColor = (value: number, price: number, ma20: number): string => {
  if (price >= ma20) return 'text-green-500 dark:text-green-400';
  return 'text-red-500 dark:text-red-400';
};

// Warna sel kolom periode — harus sama persis dgn renderRow (dipakai filter warna).
const getPeriodColorClass = (item: AccumulationData, p: string, valueFilter: string): string => {
  const pData = item.periods[p];
  if (!pData) return 'text-gray-600';
  const val = valueFilter === 'Ratio' ? pData.vol_pct : pData.val;
  if (val === 0) return 'text-gray-600';
  return getPeriodRatioColor(pData.vol_pct, pData.chg);
};

const PERIOD_REF_RE = /^[cf]*[dw]\d+$/;

const parsePeriodReference = (raw: string): CrossRefCondition | null =>
  parseCrossColumnReference(raw, PERIOD_REF_RE);

// Input numerik yang tak ter-parse ditandai merah (kolom teks bebas, tanpa validasi).
  // Input yang tiap atom-nya (dipisah and/or) bukan angka/kondisi-periode/warna → merah.
  const isInvalidNumeric = (v?: string, allowPeriodRef = false): boolean => {
    const t = (v || '').trim();
    if (!t) return false;
    const atoms = t.split(/\s+(?:and|or)\s+/i);
    return !atoms.every(a => {
      const s = a.trim();
      if (!s) return false;
      if (colorTokenCategory(s)) return true;
      if (parseNumericCondition(s)) return true;
      if (allowPeriodRef && parsePeriodReference(s)) return true;
      return false;
    });
  };

interface DebouncedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 300,
  ...props
}: DebouncedInputProps) {
  const [value, setValue] = useState(initialValue);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChangeRef.current(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]);

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

const getPeriods = (calcFilter: 'Daily' | 'Cumulative', isForeign: boolean) => {
  const cPrefix = calcFilter === 'Daily' ? '' : 'c';
  const fPrefix = isForeign ? 'f' : '';
  const fullPrefix = `${cPrefix}${fPrefix}`;

  const maxDays = 9; // d0..d9 — 10 kolom hari (tanpa weekly)

  const arr = [];
  for (let i = 0; i <= maxDays; i++) {
      arr.push(`${fullPrefix}d${i}`);
  }
  return arr.reverse();
};

export function StoryVolume() {
  const [dataPrimary, setDataPrimary] = useState<AccumulationData[]>([]);
  const [dataForeign, setDataForeign] = useState<AccumulationData[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [stockInput, setStockInput] = useState('');
  const [selectedTickers, setSelectedTickers] = useState<string[]>([]);
  const [showStockSuggestions, setShowStockSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [brokerInput, setBrokerInput] = useState('');
  const [showBrokerSuggestions, setShowBrokerSuggestions] = useState(false);
  const dropdownBrokerRef = useRef<HTMLDivElement>(null);
  
  const [valueFilter, setValueFilter] = useState<'Volume' | 'Ratio'>('Ratio');
  const [percentDenom, setPercentDenom] = useState<'Default' | 'TVol' | 'TVal' | 'NFLot' | 'NFVal'>('Default');
  const [tableFilter, setTableFilter] = useState<'By TLot' | 'By FNVol'>('By TLot');
  const [calcFilter, setCalcFilter] = useState<'Daily' | 'Cumulative'>('Daily');
  const [invFilter, setInvFilter] = useState<'All' | 'Foreign' | 'Domestic'>('All');
  const [brokerFilter, setBrokerFilter] = useState<string>('All');
  const [brokerList, setBrokerList] = useState<string[]>([]);

  // Form states (buffers changes until "Show" is clicked)
  const [formSelectedTickers, setFormSelectedTickers] = useState<string[]>([]);
  const [formValueFilter, setFormValueFilter] = useState<'Volume' | 'Ratio'>('Ratio');
  const [formPercentDenom, setFormPercentDenom] = useState<'Default' | 'TVol' | 'TVal' | 'NFLot' | 'NFVal'>('Default');
  const [formTableFilter, setFormTableFilter] = useState<'By TLot' | 'By FNVol'>('By TLot');
  const [formCalcFilter, setFormCalcFilter] = useState<'Daily' | 'Cumulative'>('Daily');
  const [formInvFilter, setFormInvFilter] = useState<'All' | 'Foreign' | 'Domestic'>('All');
  const [formBrokerFilter, setFormBrokerFilter] = useState<string>('All');

  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [fetchTrigger, setFetchTrigger] = useState(1);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isRefetching, setIsRefetching] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [accD0Map, setAccD0Map] = useState<Record<string, AccD0>>({});

  // Load broker list once — from raw/brokermetadata.parquet (same source as BrokerTransaction)
  // This is more accurate than /api/broker/list (broker_list.csv) which currently contains stale/incorrect codes.
  useEffect(() => {
    let active = true;
    fetchBrokerList().then(brokers => {
      if (!active) return;
      if (brokers && brokers.length > 0) {
        setBrokerList(brokers);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Load data from API
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      try {
        const isFirstLoad = dataPrimary.length === 0 && dataForeign.length === 0;
        if (isFirstLoad) {
          setLoading(true);
        } else {
          setIsRefetching(true);
        }
        setError(null);

        const primaryType = invFilter === 'Foreign' ? 'foreign' : (invFilter === 'Domestic' ? 'domestic' : 'all');
        const resDatesPromise = availableDates.length === 0 ? fetchVolumeDates() : Promise.resolve(null);
        const isBrokerMode = brokerFilter !== 'All';
        const part1Promise = isBrokerMode
          ? fetchBrokerVolumeData(brokerFilter, primaryType, 1)
          : fetchVolumeData(primaryType, 1);
        const part2Promise = isBrokerMode
          ? fetchBrokerVolumeData(brokerFilter, primaryType, 2)
          : fetchVolumeData(primaryType, 2);

        // A/D — D0 dari acc_dist (nilai & warna identik dgn menu Acc/Dist), paralel dgn volume
        const accType = primaryType as AccumulationDistributionType;
        const accPart1Promise = isBrokerMode
          ? fetchBrokerAccumulationDistributionData(brokerFilter, accType, 1)
          : fetchAccumulationDistributionData(accType, 1);
        const accPart2Promise = isBrokerMode
          ? fetchBrokerAccumulationDistributionData(brokerFilter, accType, 2)
          : fetchAccumulationDistributionData(accType, 2);

        const [resPrimary, resDates] = await Promise.all([
          part1Promise,
          resDatesPromise
        ]);

        if (!active) return;

        const parsedData = convertBackendToFrontend(resPrimary.data.accumulationData || []);
        if (tableFilter === 'By TLot') {
          setDataPrimary(parsedData);
          setDataForeign([]); // Not needed
        } else {
          setDataForeign(parsedData);
          setDataPrimary([]); // Not needed
        }
        
        if (resDates && resDates.success && resDates.data) {
          if (Array.isArray(resDates.data)) {
            setAvailableDates(resDates.data);
          } else if (Array.isArray(resDates.data.dates)) {
            setAvailableDates(resDates.data.dates);
          }
        }

        // Stop skeleton loading once Part 1 is displayed
        setLoading(false);
        // Keep the blue bar animating by ensuring isRefetching is true
        setIsRefetching(true);

        // Fetch Part 2 in background and append
        part2Promise.then((resPart2) => {
          if (!active) return;
          if (resPart2.success && resPart2.data.accumulationData) {
            const parsedDataPart2 = convertBackendToFrontend(resPart2.data.accumulationData);
            if (tableFilter === 'By TLot') {
              setDataPrimary(prev => {
                const existing = new Set(prev.map(d => d.symbol));
                const newItems = parsedDataPart2.filter(d => !existing.has(d.symbol));
                return [...prev, ...newItems];
              });
            } else {
              setDataForeign(prev => {
                const existing = new Set(prev.map(d => d.symbol));
                const newItems = parsedDataPart2.filter(d => !existing.has(d.symbol));
                return [...prev, ...newItems];
              });
            }
          }
        }).catch((err) => {
          console.error('Error fetching Part 2 accumulation data:', err);
        }).finally(() => {
          if (active) {
            setIsRefetching(false);
          }
        });

        // A/D: merge part1+part2 acc_dist jadi map symbol → D0
        Promise.all([accPart1Promise, accPart2Promise]).then(([acc1, acc2]) => {
          if (!active) return;
          const map = extractAccD0(acc1.data.accumulationData || []);
          Object.assign(map, extractAccD0(acc2.data.accumulationData || []));
          setAccD0Map(map);
        }).catch(() => {});

      } catch (err: any) {
        if (!active) return;
        console.error('Error accumulation data:', err);
        setError(err.message || 'Failed to load data');
        setLoading(false);
        setIsRefetching(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [fetchTrigger]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowStockSuggestions(false);
      }
      if (dropdownBrokerRef.current && !dropdownBrokerRef.current.contains(event.target as Node)) {
        setShowBrokerSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredBrokers = useMemo(() => {
    if (!brokerInput) return brokerList;
    const q = brokerInput.toLowerCase();
    return brokerList.filter(b => b.toLowerCase().includes(q));
  }, [brokerList, brokerInput]);

  const activeData = tableFilter === 'By FNVol' ? dataForeign : dataPrimary;

  const periodsTotal = useMemo(() => getPeriods(calcFilter, false), [calcFilter]);
  const periodsForeign = useMemo(() => getPeriods(calcFilter, true), [calcFilter]);

  // Get unique tickers for filter dropdown — only IHSG/IDX + 4-char
  const allTickers = useMemo(() => {
    return [...new Set(activeData.map(item => item.symbol).filter(s => s === 'IHSG' || s === 'IDX' || s.length === 4))].sort();
  }, [activeData]);

  const filteredTickers = useMemo(() => {
    return stockInput
      ? allTickers.filter(ticker => ticker.toLowerCase().includes(stockInput.toLowerCase()))
      : allTickers;
  }, [allTickers, stockInput]);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const SortIndicator = ({ col }: { col: string }) => {
    if (!sortConfig || sortConfig.key !== col) return null;
    return <span className="text-[10px]">{sortConfig.direction === 'asc' ? ' ↑' : ' ↓'}</span>;
  };

  const filterAndSortData = (dataset: AccumulationData[], periods: string[]) => {
    // Only show IHSG/IDX + 4-char tickers (hide warrants/ETCs)
    let filteredBase = dataset.filter(item => item.symbol === 'IHSG' || item.symbol === 'IDX' || item.symbol.length === 4);
    const selectedSet = new Set(selectedTickers);
    let filtered = selectedTickers.length === 0
      ? filteredBase
      : filteredBase.filter(item => selectedSet.has(item.symbol));
      
    // Filter per kolom: ekspresi `A and B or C` + token warna (merah/oren/ijo/lime/netral/kuning)
    const valType = valueFilter === 'Volume' ? 'val' : 'vol_pct';
    const is4SepData = availableDates[0]?.includes('4 Sep') ?? false;

    const pass = (expr: string | undefined, cell: FilterCell, customAtom?: AtomEvaluator): boolean => {
      if (!expr || !expr.trim()) return true;
      return customAtom
        ? evaluateFilterExpression(expr, cell, { customAtom })
        : evaluateFilterExpression(expr, cell);
    };

    // Apply column filters
    filtered = filtered.filter(item => {
      const isIdxRow = item.symbol === 'IHSG' || item.symbol === 'IDX';
      const displayPrice = (isIdxRow && is4SepData) ? 6636 : item.price;
      const displayPercent = (isIdxRow && is4SepData) ? -0.47 : item.percent1d;
      const priceColor = displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent);
      const d0Period = item.periods['d0'];
      const volColColor = (item.volume === 0 || item.price === 0 || !d0Period)
        ? 'text-gray-600'
        : getPeriodRatioColor(d0Period.vol_pct, d0Period.chg);
      const trendColor = item.trend === 'Up Trend' ? 'text-[#00ff00]' : item.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500';
      const momentumStr = item.momentum === 2 ? 'up' : item.momentum === 0 ? 'down' : 'sw';
      const momentumColor = item.momentum === 2 ? 'text-[#00ff00]' : item.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500';
      const insiderColor = item.insider === undefined ? 'text-gray-600' : item.insider > 0 ? 'text-[#00ff00]' : item.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]';

      if (!pass(columnFilters.symbol, { value: NaN, text: item.symbol.toLowerCase(), colorClass: 'text-gray-300', isNumeric: false })) return false;
      if (!pass(columnFilters.trend, { value: NaN, text: item.trend.toLowerCase(), colorClass: trendColor, isNumeric: false })) return false;
      if (!pass(columnFilters.momentum, { value: NaN, text: momentumStr, colorClass: momentumColor, isNumeric: false })) return false;
      if (!pass(columnFilters.insider, { value: item.insider ?? 0, text: String(item.insider ?? 0), colorClass: insiderColor, isNumeric: true })) return false;
      const accD0 = accD0Map[item.symbol];
      const accdistVal = !accD0 ? 0 : (valueFilter === 'Volume' ? accD0.val : accD0.vol_pct);
      if (!pass(columnFilters['accdist'], { value: accdistVal, text: String(accdistVal), colorClass: getAccColor(accD0, valueFilter), isNumeric: true })) return false;
      if (!pass(columnFilters.percent1d, { value: item.percent1d, text: String(item.percent1d), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters.price, { value: item.price, text: String(item.price), colorClass: priceColor, isNumeric: true })) return false;
      if (!pass(columnFilters.volume, { value: item.volume, text: String(item.volume), colorClass: volColColor, isNumeric: true })) return false;
      if (!pass(columnFilters.value, { value: item.value, text: String(item.value), colorClass: volColColor, isNumeric: true })) return false;

      for (const p of periods) {
        const expr = columnFilters[p];
        if (!expr || !expr.trim()) continue;
        const pData = item.periods[p];
        const pColor = getPeriodColorClass(item, p, valueFilter);
        const pVal = signedByColor(pData?.[valType as 'val' | 'vol_pct'] ?? 0, pColor);
        const cell: FilterCell = { value: pVal, text: String(pVal), colorClass: pColor, isNumeric: true };
        const periodRefAtom: AtomEvaluator = (atom, c) => {
          const ref = parsePeriodReference(atom);
          if (!ref) return null;
          const refRaw = item.periods[ref.ref]?.[valType as 'val' | 'vol_pct'] || 0;
          const refVal = signedByColor(refRaw, getPeriodColorClass(item, ref.ref, valueFilter));
          return evaluateCrossColumn(c.value, refVal, ref);
        };
        if (!evaluateFilterExpression(expr, cell, { customAtom: periodRefAtom })) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (!sortConfig) return 0;
      let aValue: any;
      let bValue: any;
      
      if (sortConfig.key === 'symbol') {
        aValue = a.symbol;
        bValue = b.symbol;
      } else if (sortConfig.key === 'price') {
        aValue = a.price;
        bValue = b.price;
      } else if (sortConfig.key === 'percent1d') {
        aValue = a.percent1d;
        bValue = b.percent1d;
      } else if (sortConfig.key === 'volume') {
        aValue = a.volume;
        bValue = b.volume;
      } else if (sortConfig.key === 'value') {
        aValue = a.value;
        bValue = b.value;
      } else if (sortConfig.key === 'trend') {
        aValue = a.trend;
        bValue = b.trend;
      } else if (sortConfig.key === 'momentum') {
        aValue = a.momentum;
        bValue = b.momentum;
      } else if (sortConfig.key === 'insider') {
        const av = a.insider;
        const bv = b.insider;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        aValue = av;
        bValue = bv;
      } else if (sortConfig.key === 'accdist') {
        const accA = accD0Map[a.symbol];
        const accB = accD0Map[b.symbol];
        aValue = !accA ? 0 : (valueFilter === 'Volume' ? accA.val : accA.vol_pct);
        bValue = !accB ? 0 : (valueFilter === 'Volume' ? accB.val : accB.vol_pct);
      } else {
        const valType = valueFilter === 'Volume' ? 'val' : 'vol_pct';
        aValue = signedByColor(a.periods[sortConfig.key]?.[valType] || 0, getPeriodColorClass(a, sortConfig.key, valueFilter));
        bValue = signedByColor(b.periods[sortConfig.key]?.[valType] || 0, getPeriodColorClass(b, sortConfig.key, valueFilter));
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const table1Ref = useRef<HTMLDivElement>(null);
  const table2Ref = useRef<HTMLDivElement>(null);

  const handleScroll = (sourceIndex: number, e: React.UIEvent<HTMLDivElement>) => {
    const targetRef = sourceIndex === 0 ? table2Ref : table1Ref;
    if (targetRef.current) {
      if (targetRef.current.scrollTop !== e.currentTarget.scrollTop) {
        targetRef.current.scrollTop = e.currentTarget.scrollTop;
      }
      if (targetRef.current.scrollLeft !== e.currentTarget.scrollLeft) {
        targetRef.current.scrollLeft = e.currentTarget.scrollLeft;
      }
    }
  };

  const sortedDataTotal = useMemo(() => {
    return filterAndSortData(dataPrimary, periodsTotal);
  }, [dataPrimary, periodsTotal, selectedTickers, columnFilters, sortConfig, valueFilter, accD0Map]);

  const sortedDataForeign = useMemo(() => {
    return filterAndSortData(dataForeign, periodsForeign);
  }, [dataForeign, periodsForeign, selectedTickers, columnFilters, sortConfig, valueFilter, accD0Map]);

  // Filter berubah → kembali ke halaman 1; slice halaman lama bikin hasil
  // filter tak terlihat (baris terbuang ada di luar page yang sedang tampil).
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);


  // Pagination calculations
  const renderTable = (sortedData: AccumulationData[], periods: string[], isForeign: boolean, tableIndex: number) => {
    const idxRow = sortedData.find(r => r.symbol === 'IHSG' || r.symbol === 'IDX');
    const remainingData = sortedData.filter(r => r.symbol !== 'IHSG' && r.symbol !== 'IDX');
    const totalPages = Math.ceil(remainingData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = remainingData.slice(startIndex, endIndex);

    const is4SepData = availableDates[0]?.includes('4 Sep');
    const renderRow = (row: AccumulationData, isIdx: boolean = false) => {
      const isIHSG4Sep = isIdx && is4SepData;
      const displayPrice = isIHSG4Sep ? 6636 : row.price;
      const displayPercent = isIHSG4Sep ? -0.47 : row.percent1d;
      const d0 = row.periods['d0'];
      const d0Color = (row.volume === 0 || row.price === 0 || !d0)
        ? 'text-gray-600'
        : getPeriodRatioColor(d0.vol_pct, d0.chg);
      const accD0 = accD0Map[row.symbol];
      const accdistVal = !accD0 ? 0 : (valueFilter === 'Volume' ? accD0.val : accD0.vol_pct);
      const accColor = getAccColor(accD0, valueFilter);
      return (
      <tr key={row.symbol} className={`hover:bg-white/[0.25] transition-colors h-[18px] divide-x divide-white/30 ${isIdx ? 'bg-blue-900/30' : ''}`}>
        <td className="px-1 py-0 text-left text-gray-300 truncate">{row.symbol === 'IDX' ? 'IHSG' : row.symbol}</td>
        <td className={`px-1 py-0 text-center truncate ${row.trend === 'Up Trend' ? 'text-[#00ff00]' : row.trend === 'Down Trend' ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.trend === 'Up Trend' ? 'UP' : row.trend === 'Down Trend' ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-center truncate ${row.momentum === 2 ? 'text-[#00ff00]' : row.momentum === 0 ? 'text-[#ff0000]' : 'text-gray-500'}`}>{row.momentum === 2 ? 'UP' : row.momentum === 0 ? 'DOWN' : 'SW'}</td>
        <td className={`px-1 py-0 text-right truncate ${row.insider === undefined ? 'text-gray-600' : row.insider > 0 ? 'text-[#00ff00]' : row.insider < 0 ? 'text-[#ff0000]' : 'text-[#ffff00]'}`}>
          {row.insider === undefined ? '-' : formatNumberWithAbbreviation(row.insider)}
        </td>
        <td className={`px-1 py-0 text-right truncate ${accColor}`}>
          {!accD0 || accdistVal === 0
            ? '-'
            : (valueFilter === 'Volume' ? formatNumberWithAbbreviation(accdistVal) : accdistVal.toFixed(1) + '%')}
        </td>
        {periods.map((p) => {
          const pData = row.periods[p];
          if (!pData) return <td key={p} className="px-1 py-0 text-right text-gray-600">-</td>;
          let val = valueFilter === 'Ratio' ? pData.vol_pct : pData.val;
          const colorClass =
            val === 0
              ? 'text-gray-600'
              : getPeriodRatioColor(pData.vol_pct, pData.chg);
          const negPrefix = (colorClass === 'text-[#ff0000]' || colorClass === 'text-[#ffa500]') && val > 0 ? '-' : '';
          return (
            <td key={p} className={`px-1 py-0 text-right truncate ${colorClass}`}>
              {val === 0 ? '-' : negPrefix + (valueFilter === 'Volume' ? formatNumberWithAbbreviation(val) : val.toFixed(2))}
            </td>
          );
        })}
        <td className={`px-1 py-0 text-right truncate ${displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent)}`}>
           {displayPrice === 0 ? '-' : displayPercent.toFixed(1) + '%'}
         </td>
         <td className={`px-1 py-0 text-right truncate ${displayPrice === 0 ? 'text-gray-600' : getPercentChgColor(displayPercent)}`}>
           {displayPrice === 0 ? '-' : new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(displayPrice)}
         </td>
        <td className={`px-1 py-0 text-right truncate ${d0Color}`}>
          {row.volume === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.volume)}
        </td>
        <td className={`px-1 py-0 text-right truncate ${d0Color}`}>
          {row.value === 0 || row.price === 0 ? '-' : formatNumberWithAbbreviation(row.value)}
        </td>
      </tr>
      );
    };

     return (
      <div className="flex flex-col bg-black flex-auto min-h-0 w-full border-t border-white/30">
        <div 
          className="flex-auto overflow-y-auto overflow-x-auto" 
          style={{ willChange: 'transform' }}
          ref={tableIndex === 0 ? table1Ref : table2Ref}
          onScroll={(e) => handleScroll(tableIndex, e)}
        >
          <table className="w-full text-[12px] tabular-nums table-fixed min-w-[600px] leading-tight border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-900/80 backdrop-blur-md text-white font-bold z-10" style={{ transform: 'translateZ(0)' }}>
              <tr className="cursor-pointer select-none h-[22px] divide-x divide-white/30">
                <th onClick={() => handleSort('symbol')} className="px-1 py-0 text-center w-[60px]">Ticker<SortIndicator col="symbol" /></th>
                <th onClick={() => handleSort('trend')} className="px-1 py-0 text-center w-[60px]">Trend<SortIndicator col="trend" /></th>
                <th onClick={() => handleSort('momentum')} className="px-1 py-0 text-center w-[60px]">Momentum<SortIndicator col="momentum" /></th>
                <th onClick={() => handleSort('insider')} className="px-1 py-0 text-center w-[60px]" title="5 hari terakhir">Insider<SortIndicator col="insider" /></th>
                <th onClick={() => handleSort('accdist')} className="px-1 py-0 text-center w-[60px]">Acc/Dist<SortIndicator col="accdist" /></th>
                {periods.map((p) => {
                  let rawLabel = isForeign ? p.replace('f', '').toUpperCase() : p.toUpperCase();
                  let label = rawLabel;
                  let thTitle = rawLabel;
                  
                  // Handle D0..D5 and CD0..CD5
                  const dMatch = rawLabel.match(/^(?:C)?D(\d+)$/);
                  if (dMatch && availableDates.length > 0) {
                     const dIndex = parseInt(dMatch[1]);
                     if (!isNaN(dIndex) && dIndex < availableDates.length) {
                        const dateStr = availableDates[dIndex];
                        if (dateStr) {
                           const parts = dateStr.split('-');
                           if (parts.length === 3) {
                             const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                             const day = parseInt(parts[2], 10);
                             const monthStr = months[parseInt(parts[1], 10) - 1];
                             label = `${day} ${monthStr}`;
                             thTitle = `${rawLabel} — ${day} ${monthStr} ${parts[0]}`;
                           } else {
                             label = dateStr;
                             thTitle = `${rawLabel} — ${dateStr}`;
                           }
                        }
                     }
                  }

                  let sectionBg = '';
                  if (rawLabel === 'D0' || rawLabel === 'CD0') sectionBg = 'bg-emerald-500/20';
                  else if (rawLabel.startsWith('D') || rawLabel.startsWith('CD')) sectionBg = 'bg-blue-500/20';
                  else if (rawLabel.startsWith('W') || rawLabel.startsWith('CW')) sectionBg = 'bg-purple-500/20';
                  return <th key={p} title={thTitle} onClick={() => handleSort(p)} className={`px-1 py-0 text-center w-[60px] ${sectionBg}`}>{label}<SortIndicator col={p} /></th>;
                })}
                <th onClick={() => handleSort('percent1d')} className="px-1 py-0 text-center w-[60px] bg-emerald-500/20">%<SortIndicator col="percent1d" /></th>
                <th onClick={() => handleSort('price')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Price<SortIndicator col="price" /></th>
                <th onClick={() => handleSort('volume')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Volume<SortIndicator col="volume" /></th>
                <th onClick={() => handleSort('value')} className="px-1 py-0 text-center w-[70px] bg-emerald-500/20">Value<SortIndicator col="value" /></th>
              </tr>
              {/* Interactive filter row */}
              <tr className="bg-gray-800/80 backdrop-blur-md h-[22px] divide-x divide-white/30">
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-left text-white placeholder:text-gray-500 focus:outline-none" placeholder="Filter" value={columnFilters.symbol || ''} onChange={(val) => setColumnFilters(prev => ({...prev, symbol: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters.trend || ''} onChange={(val) => setColumnFilters(prev => ({...prev, trend: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className="w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none" placeholder="UP" value={columnFilters.momentum || ''} onChange={(val) => setColumnFilters(prev => ({...prev, momentum: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-center text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['insider']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder=">1B" value={columnFilters.insider || ''} onChange={(val) => setColumnFilters(prev => ({...prev, insider: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['accdist']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder={valueFilter === 'Volume' ? '>1M' : '>5'} value={columnFilters['accdist'] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, accdist: val}))} />
                </td>
                {periods.map((p) => (
                  <td key={p} className="px-1 py-0">
                    <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters[p], true) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder={valueFilter === 'Volume' ? '>1M' : '>5'} title=">5 (Ratio), >1M (Volume), >D1, >D1+1M, >(D1/2)" value={columnFilters[p] || ''} onChange={(val) => setColumnFilters(prev => ({...prev, [p]: val}))} />
                  </td>
                ))}
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['percent1d']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder=">5" value={columnFilters.percent1d || ''} onChange={(val) => setColumnFilters(prev => ({...prev, percent1d: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['price']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder="<200" value={columnFilters.price || ''} onChange={(val) => setColumnFilters(prev => ({...prev, price: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['volume']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder=">1M" value={columnFilters.volume || ''} onChange={(val) => setColumnFilters(prev => ({...prev, volume: val}))} />
                </td>
                <td className="px-1 py-0">
                  <DebouncedInput type="text" className={`w-full bg-transparent text-[12px] text-right text-white placeholder:text-gray-500 focus:outline-none${isInvalidNumeric(columnFilters['value']) ? ' border border-red-500/70 rounded-sm' : ''}`} placeholder=">1B" value={columnFilters.value || ''} onChange={(val) => setColumnFilters(prev => ({...prev, value: val}))} />
                </td>
              </tr>
            </thead>
            <tbody className="divide-y-0">
              {idxRow && renderRow(idxRow, true)}
              {paginatedData.map((row) => renderRow(row))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const resetSettings = () => {
    setSortConfig(null);
    setStockInput('');
    setSelectedTickers([]);
    setFormSelectedTickers([]);
    setShowStockSuggestions(false);
    setValueFilter('Ratio');
    setFormValueFilter('Ratio');
    setPercentDenom('Default');
    setFormPercentDenom('Default');
    setTableFilter('By TLot');
    setFormTableFilter('By TLot');
    setCalcFilter('Daily');
    setFormCalcFilter('Daily');
    setInvFilter('All');
    setFormInvFilter('All');
    setBrokerFilter('All');
    setFormBrokerFilter('All');
    setColumnFilters({});
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleShow = () => {
    setSelectedTickers(formSelectedTickers);
    setValueFilter(formValueFilter);
    setPercentDenom(formPercentDenom);
    setTableFilter(formTableFilter);
    setCalcFilter(formCalcFilter);
    setInvFilter(formInvFilter);
    setBrokerFilter(formBrokerFilter);
    setCurrentPage(1);
    setFetchTrigger(prev => prev + 1);
  };

  const handleTickerSelect = (ticker: string) => {
    if (formSelectedTickers.includes(ticker)) {
      setFormSelectedTickers(formSelectedTickers.filter(t => t !== ticker));
    } else {
      setFormSelectedTickers([...formSelectedTickers, ticker]);
    }
  };



  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <div className="text-center">
          <p className="text-red-500 mb-4 text-sm">Error: {error}</p>
          <Button onClick={() => window.location.reload()} variant="outline" size="sm">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const activeSortedData = tableFilter === 'By FNVol' ? sortedDataForeign : sortedDataTotal;
  const totalPages = Math.ceil(activeSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-2 relative">
      {(isRefetching || loading) && (
        <div className="absolute top-0 left-0 w-full h-1 z-50 overflow-hidden bg-[#3a4252]">
          <div className="w-full h-full bg-blue-500 animate-[loading_1.5s_ease-in-out_infinite]" style={{ transformOrigin: 'left' }}></div>
        </div>
      )}
      <style>{`
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
      {/* Top Control Bar */}
      <div className="bg-card border-b border-[#3a4252] px-4 py-1.5 shrink-0 lg:sticky lg:top-0 lg:z-40">
        <div className="flex flex-col md:flex-row md:flex-wrap items-center gap-2 md:gap-x-7 md:gap-y-2">
          
          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Ticker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownRef}>
              <div className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground hover:border-primary/50 focus-within:border-primary/50 transition-colors">
                <Search className="w-3.5 h-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <input
                  type="text"
                  className="w-full bg-transparent text-sm text-foreground focus:outline-none placeholder:text-muted-foreground/50 truncate"
                  placeholder="Select ticker..."
                  value={showStockSuggestions ? stockInput : (formSelectedTickers.length > 0 ? formSelectedTickers.join(', ') : '')}
                  onChange={(e) => {
                    setStockInput(e.target.value);
                    setShowStockSuggestions(true);
                  }}
                  onFocus={() => setShowStockSuggestions(true)}
                />
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0 cursor-pointer" onClick={() => setShowStockSuggestions(!showStockSuggestions)} />
              </div>
              {showStockSuggestions && (
                <div id="stock-suggestions" role="listbox" className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-full overflow-hidden flex flex-col">
                  {formSelectedTickers.length > 0 && (
                    <div className="px-3 py-2 text-xs text-muted-foreground border-b border-[#3a4252] flex items-center justify-between bg-popover">
                      <span>Selected ({formSelectedTickers.length})</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormSelectedTickers([]);
                        }}
                        className="text-xs text-destructive hover:text-destructive/80 font-medium"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="max-h-60 overflow-y-auto py-1">
                    {(() => {
                      const filteredList = stockInput === ''
                        ? allTickers.slice(0, 30)
                        : filteredTickers.slice(0, 30);
                      return filteredList.map(stock => {
                        const isSelected = formSelectedTickers.includes(stock);
                        return (
                          <div
                            key={stock}
                            className={`px-3 py-[2.06px] text-sm cursor-pointer hover:bg-accent flex justify-between items-center ${isSelected ? 'text-primary font-medium bg-accent/50' : 'text-foreground'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTickerSelect(stock);
                            }}
                          >
                            <span>{stock}</span>
                            {isSelected && <span className="text-primary text-xs">✓</span>}
                          </div>
                        );
                      });
                    })()}
                    {stockInput !== '' && filteredTickers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">No stocks found</div>
                    )}
                    {stockInput === '' && allTickers.length > 30 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t border-[#3a4252]">
                        Type to search {allTickers.length - 30} more stocks
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">View:</label>
            <select
              value={formCalcFilter}
              onChange={(e) => {
                setFormCalcFilter(e.target.value as 'Daily' | 'Cumulative');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="Daily">Daily</option>
              <option value="Cumulative">Cumulative</option>
            </select>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Data:</label>
            <div className="flex items-center gap-1">
              <select
                value={formValueFilter}
                onChange={(e) => {
                  setFormValueFilter(e.target.value as 'Volume' | 'Ratio');
                }}
                className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
              >
                <option value="Volume">Volume</option>
                <option value="Ratio">Ratio</option>
              </select>
            </div>
          </div>



          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">F/D:</label>
            <select
              value={formInvFilter}
              onChange={(e) => {
                setFormInvFilter(e.target.value as 'All' | 'Foreign' | 'Domestic');
              }}
              className="h-9 px-3 border border-[#3a4252] rounded-md bg-background text-foreground text-sm w-full md:w-auto"
            >
              <option value="All">All</option>
              <option value="Foreign">Foreign</option>
              <option value="Domestic">Domestic</option>
            </select>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
            <label className="text-sm font-medium whitespace-nowrap">Broker:</label>
            <div className="relative flex-1 md:flex-none" ref={dropdownBrokerRef}>
              <div
                className="w-full md:w-32 h-9 px-3 flex items-center justify-between bg-background border border-input rounded-md text-sm text-foreground cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setShowBrokerSuggestions(!showBrokerSuggestions)}
              >
                <span className={`truncate ${formBrokerFilter !== 'All' ? getBrokerColorClass(formBrokerFilter) : ''}`}>{formBrokerFilter === 'All' ? 'ALL (Default)' : formBrokerFilter}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground opacity-50 ml-1 flex-shrink-0" />
              </div>
              {showBrokerSuggestions && (
                <div className="absolute top-full left-0 mt-1 bg-popover border border-[#3a4252] rounded-md shadow-lg z-50 w-48 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-[#3a4252]">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        className="w-full h-8 pl-7 pr-2 bg-[#0a0f20] border border-[#3a4252] rounded text-sm text-white focus:outline-none focus:border-primary/50"
                        placeholder="Search broker..."
                        value={brokerInput}
                        onChange={(e) => setBrokerInput(e.target.value.toUpperCase())}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto py-1">
                    <div
                      className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${formBrokerFilter === 'All' ? 'text-primary font-medium bg-accent/50' : 'text-foreground hover:bg-accent'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormBrokerFilter('All');
                        setBrokerInput('');
                        setShowBrokerSuggestions(false);
                      }}
                    >
                      <span>ALL (Default)</span>
                      {formBrokerFilter === 'All' && <span className="text-primary text-xs">✓</span>}
                    </div>
                    {filteredBrokers.slice(0, 100).map(broker => {
                      const isSelected = formBrokerFilter === broker;
                      return (
                        <div
                          key={broker}
                          className={`px-3 py-[2.06px] text-sm cursor-pointer flex justify-between items-center ${isSelected ? 'font-medium bg-accent/50' : 'hover:bg-accent'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormBrokerFilter(broker);
                            setBrokerInput('');
                            setShowBrokerSuggestions(false);
                          }}
                        >
                          <span className={getBrokerColorClass(broker)}>{broker}</span>
                          {isSelected && <span className="text-primary text-xs">✓</span>}
                        </div>
                      );
                    })}
                    {filteredBrokers.length === 0 && (
                      <div className="px-3 py-2 text-sm text-muted-foreground text-center">No brokers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto ml-auto">
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={handleShow} className="h-8 text-sm px-4">
                Show
              </Button>
              <Button onClick={resetSettings} variant="outline" size="sm" className="flex items-center gap-2 h-8">
                <Undo2 className="w-4 h-4 rotate-180" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full animate-pulse">
          <div className="h-[26px] w-full bg-gray-900 border-b border-white/30 shrink-0 flex items-center px-2">
            <div className="h-3 w-16 bg-slate-700/40 rounded"></div>
          </div>
          <div className="flex-1 p-2 space-y-3">
            {/* Header row */}
            <div className="flex gap-2 pb-2 border-b border-white/20">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="h-4 flex-1 bg-slate-700/30 dark:bg-slate-600/50 rounded"></div>
              ))}
            </div>
            {/* Data rows */}
            {[...Array(20)].map((_, i) => (
              <div key={i} className="flex gap-2">
                <div className="h-3.5 w-12 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                {[...Array(11)].map((_, j) => (
                  <div key={j} className="h-3.5 flex-1 bg-slate-700/20 dark:bg-slate-700/40 rounded"></div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      ) : (
      <Card className="flex flex-col flex-1 min-h-0 p-0 bg-black border-gray-800 overflow-hidden rounded-none w-full">
        <div className={`flex flex-col flex-1 min-h-0`}>
          {tableFilter === 'By TLot' && (
            <div className="flex flex-col min-h-0 flex-auto w-full">
               <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30 shrink-0">By TLot</h3>
               {renderTable(sortedDataTotal, periodsTotal, false, 0)}
            </div>
          )}

          {tableFilter === 'By FNVol' && (
            <div className="flex flex-col min-h-0 flex-auto w-full">
               <h3 className="text-[12px] font-bold px-2 py-1 bg-gray-900 text-white border-b border-white/30 shrink-0">By FNVol</h3>
               {renderTable(sortedDataForeign, periodsForeign, true, 1)}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {(activeSortedData.length > 0) && (
          <div className="border-t border-border bg-muted/30 px-2 py-1 flex flex-col sm:flex-row items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-1 py-0.5 text-[11px] border border-border rounded-md bg-background text-foreground h-6"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <span>
                Showing {startIndex + 1} - {Math.min(endIndex, activeSortedData.length)} of {activeSortedData.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="First page"
              >
                <ChevronFirst className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-[11px] text-foreground px-1">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1 h-6 w-6 flex items-center justify-center border border-border rounded-md bg-background text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-accent transition-colors"
                aria-label="Last page"
              >
                <ChevronLast className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </Card>
      )}
    </div>
  );
}
